<template>
  <div class="deal-kanban">
    <div class="deal-kanban__columns">
      <div
        v-for="stage in stages"
        :key="stage.id"
        class="deal-kanban__column"
        :style="{ backgroundColor: columnBackground(stage) }"
      >
        <!-- Column Header -->
        <div class="deal-kanban__column-header">
          <div class="deal-kanban__column-title">
            <span
              class="deal-kanban__stage-badge"
              :style="{ backgroundColor: stageColor(stage), color: stageTextColor(stage) }"
            >
              {{ stage.name }}
            </span>
            <span class="deal-kanban__column-value">
              {{ formatCurrency(stage.total_value) }}
            </span>
          </div>
          <DropdownMenu>
            <template #trigger>
              <button
                type="button"
                class="deal-kanban__column-menu"
                :aria-label="$t('deals.stage_actions')"
              >
                <img src="/icons/pipeline-column-menu.svg" width="10" height="2" alt="" />
              </button>
            </template>
            <template #items>
              <p class="deal-kanban__menu-count">
                {{ $t('deals.stage_deal_count', { count: stageDealCount(stage) }) }}
              </p>
              <button type="button" class="dropdown-menu__item" @click="$emit('add-deal', stage)">
                {{ $t('deals.stage_add_deal') }}
              </button>
            </template>
          </DropdownMenu>
        </div>

        <!-- Draggable List -->
        <div class="deal-kanban__body">
          <draggable
            :list="stage.deals"
            group="deals"
            item-key="id"
            class="deal-kanban__list"
            ghost-class="deal-kanban__card--ghost"
            drag-class="deal-kanban__card--dragging"
            :disabled="isTouchDevice"
            @change="(evt) => handleDragChange(evt, stage)"
          >
            <template #item="{ element: deal }">
              <DealCard
                :deal="deal"
                :members="members"
                :selected="selectedDealIds.includes(deal.id)"
                @click="$emit('card-click', deal)"
                @select="(event) => $emit('card-select', deal.id, event)"
                @email="$emit('card-email', deal)"
                @assign-owner="(ownerId) => $emit('assign-owner', deal, ownerId)"
              />
            </template>
          </draggable>

          <div class="deal-kanban__empty" v-if="!stage.deals || stage.deals.length === 0">
            <span>{{ $t('deals.no_deals_in_stage') }}</span>
          </div>
        </div>
      </div>

      <div
        class="deal-kanban__column deal-kanban__column--archive"
        :class="{ 'deal-kanban__column--collapsed': !archiveExpanded }"
        ref="archiveColumnEl"
        :role="archiveExpanded ? undefined : 'button'"
        :tabindex="archiveExpanded ? undefined : 0"
        :aria-expanded="archiveExpanded"
        :aria-label="archiveExpanded ? undefined : $t('deals.archive_expand')"
        @click="onArchiveColumnClick"
        @keydown.enter.prevent="expandArchive"
        @keydown.space.prevent="expandArchive"
      >
        <div v-if="!archiveExpanded" class="deal-kanban__archive-rail">
          <span class="deal-kanban__archive-count">{{ archiveCount }}</span>
          <span class="deal-kanban__archive-label">{{ $t('deals.archive_column') }}</span>
        </div>

        <div v-else class="deal-kanban__column-header" @click.stop>
          <div class="deal-kanban__column-title">
            <span class="deal-kanban__stage-badge deal-kanban__stage-badge--archive">
              {{ $t('deals.archive_column') }}
            </span>
            <span class="deal-kanban__column-value">
              {{ formatCurrency(archiveTotal) }}
            </span>
          </div>
          <button
            type="button"
            class="deal-kanban__column-menu"
            :aria-label="$t('deals.archive_collapse')"
            @click="collapseArchive"
          >
            <BaseIcon name="chevron" size="14" class="deal-kanban__archive-chevron" />
          </button>
        </div>

        <div
          class="deal-kanban__body"
          :class="{ 'deal-kanban__body--collapsed': !archiveExpanded }"
        >
          <draggable
            :list="archiveDeals"
            group="deals"
            item-key="id"
            class="deal-kanban__list"
            ghost-class="deal-kanban__card--ghost"
            drag-class="deal-kanban__card--dragging"
            :disabled="isTouchDevice"
            @change="handleArchiveDragChange"
          >
            <template #item="{ element: deal }">
              <DealCard
                v-if="archiveExpanded"
                :deal="deal"
                :members="members"
                :selected="selectedDealIds.includes(deal.id)"
                @click="$emit('card-click', deal)"
                @select="(event) => $emit('card-select', deal.id, event)"
                @email="$emit('card-email', deal)"
                @assign-owner="(ownerId) => $emit('assign-owner', deal, ownerId)"
              />
              <span v-else class="deal-kanban__archive-placeholder"></span>
            </template>
          </draggable>

          <div class="deal-kanban__empty" v-if="archiveExpanded && archiveCount === 0">
            <span>{{ $t('deals.archive_drop') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue';
  import draggable from 'vuedraggable';
  import DealCard from '@/components/deals/DealCard.vue';
  import DropdownMenu from '@/components/partials/DropdownMenu.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { readableTextColor } from '@shared/utils/colorContrast';

  export default {
    name: 'DealKanbanBoard',

    components: {
      draggable,
      DealCard,
      DropdownMenu,
      BaseIcon,
    },

    props: {
      stages: {
        type: Array,
        required: true,
        default: () => [],
      },
      selectedDealIds: {
        type: Array,
        default: () => [],
      },
      members: {
        type: Array,
        default: () => [],
      },
      archive: {
        type: Object,
        default: () => ({ deals: [], deals_count: 0, total_value: 0 }),
      },
      archiveExpanded: {
        type: Boolean,
        default: false,
      },
    },

    emits: [
      'card-click',
      'card-select',
      'card-email',
      'assign-owner',
      'stage-change',
      'add-deal',
      'archive-deal',
      'update:archiveExpanded',
    ],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      const DEFAULT_STAGE_COLOR = '#434f6f';

      const stageColor = (stage) => stage.color || DEFAULT_STAGE_COLOR;

      // Stage colours are user-picked, so white text is not always readable
      const stageTextColor = (stage) => readableTextColor(stageColor(stage), { dark: '#18181b' });

      // Columns are tinted with their own stage colour; `14` is an 8% alpha channel
      const columnBackground = (stage) => {
        const color = stageColor(stage);
        return /^#[0-9a-f]{6}$/i.test(color) ? `${color}14` : '#f3f3f3';
      };

      const stageDealCount = (stage) => stage.deals_count ?? stage.deals?.length ?? 0;

      const archiveDeals = computed(() => props.archive?.deals || []);

      const archiveCount = computed(() => archiveDeals.value.length);

      const archiveTotal = computed(() =>
        archiveDeals.value.reduce((sum, deal) => sum + (Number(deal.value) || 0), 0)
      );

      const archiveColumnEl = ref(null);

      const expandArchive = () => {
        if (!props.archiveExpanded) emit('update:archiveExpanded', true);
      };

      const collapseArchive = () => {
        emit('update:archiveExpanded', false);
      };

      const onArchiveColumnClick = () => {
        if (!props.archiveExpanded) expandArchive();
      };

      watch(
        () => props.archiveExpanded,
        (expanded) => {
          if (!expanded) return;
          nextTick(() => {
            archiveColumnEl.value?.scrollIntoView({ inline: 'end', block: 'nearest' });
          });
        }
      );

      const formatCurrency = (value) => {
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0);
      };

      const handleDragChange = (evt, newStage) => {
        if (!evt.added) return;

        const deal = evt.added.element;
        const fromArchive = !!deal.archived_at;
        const oldStageId = deal.stage_id;
        const newStageId = newStage.id;

        if (fromArchive) deal.archived_at = null;

        if (fromArchive || oldStageId !== newStageId) {
          emit('stage-change', deal.id, newStageId, oldStageId, fromArchive);
        }
      };

      const handleArchiveDragChange = (evt) => {
        if (!evt.added) return;
        expandArchive();
        emit('archive-deal', evt.added.element.id);
      };

      return {
        stageColor,
        stageTextColor,
        columnBackground,
        stageDealCount,
        archiveDeals,
        archiveCount,
        archiveTotal,
        archiveColumnEl,
        expandArchive,
        collapseArchive,
        onArchiveColumnClick,
        formatCurrency,
        handleDragChange,
        handleArchiveDragChange,
        isTouchDevice,
        $t,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-kanban {
    overflow-x: auto;
    height: calc(100vh - 192px);
    padding-bottom: 0.5rem;
    @include lg-down {
      height: calc(100vh - 320px);
      margin: 0 -1rem;
      padding-left: 1rem;
    }

    &__columns {
      display: flex;
      gap: 16px;
      height: 100%;
      width: max-content;
      min-width: 100%;
    }

    &__column {
      flex: 0 0 308px;
      width: 308px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      height: 100%;
      min-height: 0;
      padding: 10px;
      border-radius: 6px;

      &--archive {
        margin-left: auto;
        background: #e6e8ee;
      }

      &--collapsed {
        position: sticky;
        right: 0;
        z-index: 3;
        flex-basis: 44px;
        width: 44px;
        padding: 10px 4px;
        cursor: pointer;
        box-shadow: -8px 0 16px rgb(0 9 33 / 6%);

        &:focus-visible {
          outline: 2px solid #2f69ff;
          outline-offset: 2px;
        }
      }
    }

    &__column-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }

    &__column-title {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }

    &__stage-badge {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
      max-width: 180px;
      height: 24px;
      padding: 0 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 500;
      line-height: 1;
      text-transform: uppercase;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &--archive {
        background: #434f6f;
        color: #fff;
      }
    }

    &__column-value {
      flex-shrink: 0;
      font-size: 13px;
      font-weight: 500;
      line-height: 1;
      color: #434f6f;
    }

    &__column-menu {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      padding: 0;
      border: 0;
      border-radius: 4px;
      background: transparent;
      cursor: pointer;

      @include hover {
        background: rgb(0 9 33 / 6%);
      }

      &:focus-visible {
        outline: 2px solid #2f69ff;
        outline-offset: 2px;
      }

      img {
        display: block;
      }
    }

    &__menu-count {
      margin: 0;
      padding: 0.25rem 0.5rem 0.5rem;
      color: var(--slate-400);
      font-size: 0.75rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.025em;
    }

    &__body {
      position: relative;
      display: flex;
      flex: 1;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;

      &--collapsed {
        position: absolute;
        inset: 0;
        opacity: 0;

        .deal-kanban__list {
          height: 100%;
          min-height: 100%;
        }
      }
    }

    &__list {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 4px;
      min-height: 80px;
      overflow-y: auto;
    }

    &__card {
      &--ghost {
        opacity: 0.5;
        background: var(--brand-50);
        border: 2px dashed var(--brand-300);
      }

      &--dragging {
        transform: rotate(-2deg);
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2);
      }
    }

    &__empty {
      position: absolute;
      top: 8px;
      right: 0;
      left: 0;
      color: #434f6f;
      font-size: 13px;
      font-weight: 300;
      line-height: 20px;
      text-align: center;
      pointer-events: none;
    }

    &__archive-rail {
      display: flex;
      flex: 1;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      min-height: 0;
      padding-top: 4px;
      pointer-events: none;
    }

    &__archive-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 22px;
      height: 22px;
      padding: 0 6px;
      border-radius: 999px;
      background: #434f6f;
      color: #fff;
      font-size: 11px;
      font-weight: 600;
      line-height: 1;
    }

    &__archive-label {
      color: #434f6f;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.08em;
      line-height: 1;
      text-transform: uppercase;
      writing-mode: vertical-rl;
      transform: rotate(180deg);
    }

    &__archive-chevron {
      transform: rotate(90deg);
    }

    &__archive-placeholder {
      display: block;
      height: 0;
      overflow: hidden;
    }
  }
</style>
